import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { formatBookingStatus, bookingStatusDotClass } from "../../lib/bookingStatus";
import { formatTripRange, formatLocalTimestamp, bookingTimeZoneLabel } from "../../lib/dateLocal";
import { formatMoney } from "../../lib/currencyDisplay";

function Row({ label, children, testId }) {
  if (children == null || children === "") return null;
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-[var(--zc-border)] last:border-0" data-testid={testId}>
      <span className="text-[var(--zc-text-3)] text-sm shrink-0">{label}</span>
      <span className="text-sm text-right font-medium min-w-0 break-words">{children}</span>
    </div>
  );
}

export default function AdminPartnerBookingDetails({ booking, open, onClose }) {
  if (!booking) return null;

  const b = booking;
  const snap = b.vehicle_snapshot || {};
  const currency = (b.currency || "USD").toUpperCase();

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="sm:max-w-md" data-testid="admin-partner-booking-details">
        <DialogHeader>
          <DialogTitle className="text-lg">
            {snap.year} {snap.make} {snap.model}
          </DialogTitle>
          <DialogDescription className="sr-only">Partner booking details</DialogDescription>
        </DialogHeader>

        <div className="space-y-0">
          <Row label="Status" testId="apd-status">
            <span className="inline-flex items-center gap-2">
              <span className={"dot " + bookingStatusDotClass(b.status)} />
              {formatBookingStatus(b)}
            </span>
          </Row>
          <Row label="Partner" testId="apd-partner">{b.external_partner}</Row>
          <Row label="Confirmation #" testId="apd-conf-number">{b.partner_confirmation_number}</Row>
          <Row label="Booking ID" testId="apd-booking-id">
            <span className="font-mono text-xs">{b.booking_id}</span>
          </Row>
          <Row label="External ID" testId="apd-external-id">
            <span className="font-mono text-xs">{b.external_id}</span>
          </Row>
          <Row label="Payment" testId="apd-payment">{b.payment_method?.replace(/_/g, " ")}</Row>
          <Row label="Booked on" testId="apd-created-at">
            {formatLocalTimestamp(b.created_at, b.booking_timezone) || "—"}
          </Row>
          <Row label="Trip dates" testId="apd-dates">
            <div className="text-xs">
              <div>{formatTripRange(b.start_date, b.end_date) || "—"}</div>
              {bookingTimeZoneLabel(b) && (
                <div className="text-[var(--zc-text-3)] mt-0.5">{bookingTimeZoneLabel(b)}</div>
              )}
            </div>
          </Row>
          <Row label="Pickup" testId="apd-pickup">{b.pickup_location}</Row>
          <Row label="Dropoff" testId="apd-dropoff">{b.dropoff_location}</Row>
          <Row label="Total" testId="apd-total">{formatMoney(b.total, currency)}</Row>
          <Row label="Renter" testId="apd-renter">{b.renter_name}</Row>
          <Row label="Phone" testId="apd-phone">{b.contact_phone}</Row>
          <Row label="Notes" testId="apd-notes">{b.notes}</Row>
          {b.partner_auto_confirmed && (
            <Row label="Auto-confirmed" testId="apd-auto-confirmed">Yes</Row>
          )}
          {b.partner_fulfilled_at && (
            <Row label="Fulfilled at" testId="apd-fulfilled-at">
              {formatLocalTimestamp(b.partner_fulfilled_at, b.booking_timezone) || b.partner_fulfilled_at}
            </Row>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
